import { auth, db, app } from "@/firebaseApp";
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from "firebase/firestore";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions(app);
const RecoverPassword = httpsCallable(functions, "users-recoverPassword")
// const assignRole = httpsCallable(functions, "users-assignRole")

async function userSetup(getters, commit, user) {
    const usersCollection = collection(db, "users")
    const userRef = await getDoc(doc(usersCollection, user.id))
    const userData = userRef.data()
    if (userData) {
        user.name = userData.name
        user.email = userData.email
    }
    if (user.consumer) {
        let consumer = getters['consumers/id'](user.consumer)
        console.log(consumer)
    }
    commit("add", user)
    return
}

const users = {
    namespaced: true,
    state: {
        all: [],
        user: null,
        userInfo: null,
        currentUser: null,
        errorCode: null,
        recoverEmailSent: null,
        role: null,
        unsubs: [],
    },
    getters: {
        consumer: (state) => (consumerId) => {
            return state.all.filter(({ consumer }) => {
                return consumer == consumerId
            })
        },
        invited: (state) => (name, email) => {
            return state.all.find((user) => {
                return user.email == email &&
                    user.name == name &&
                    user.state == 'invited'
            })
        },
        userId: (state) => (userId) => {
            return state.all.find(({ id }) => id == userId)
        },
        worker: (state) => (workerId) => {
            return state.all.filter(({ worker }) => {
                return worker == workerId
            })
        },
    },
    mutations: {
        add(state, user) {
            const index = state.all.findIndex(({ id }) => user.id === id)
            if (index < 0) {
                state.all.push(user);
                return
            }
            state.all.splice(index, 1, user)
        },
        addUnsub(state, unsub) {
            state.unsubs.push(unsub)
        },
        user(state, user) {
            state.user = user;
        },
        updateUser(state, updatedUser) {
            const index = state.all.findIndex(user => user.id === updatedUser.id);
            if (index !== -1) {
                state.all.splice(index, 1, updatedUser);
            }
        },
        setCurrentUser(state, user) {
            state.currentUser = user
        },
        setErrorCode(state, error) {
            state.errorCode = error
        },
        setRecoverEmailSent(state, recoverEmailSent) {
            state.recoverEmailSent = recoverEmailSent
        },
        setUserInfo(state, info) {
            state.userInfo = info
        },
        setWorkers(state, workers) {
            state.workers = workers;
        },
        delete(state, user) {
            const index = state.all.findIndex(({ id }) => user.id === id)
            if (index < 0) {
                return
            }
            state.all.splice(index, 1)
        },
        unsub(state) {
            while (state.unsubs.length) {
                let unsub = state.unsubs.pop()
                unsub()
            }
        },
        setCurrentUserRole(state, role) {
            state.role = role;
        },
    },

    actions: {
        async create({ rootState }, { name, email }) {
            const instanceId = rootState.instanceId
            if (instanceId == null) {
                return
            }
            const currentUser = rootState.users.currentUser
            if (currentUser == null) {
                return
            }
            try {
                await setDoc(doc(db, `instances/${instanceId}/invited`, email), {
                    name,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser.uid,
                    role: "worker",
                });
            } catch (error) {
                console.log(error);
            }
        },
        async getAll({ commit, rootState, rootGetters }) {
            if (rootState.instanceId == null) {
                return
            }
            const instanceId = rootState.instanceId
            commit('unsub')
            try {
                const unsub = onSnapshot(collection(db, `instances/${instanceId}/users`), (querySnapshot) => {
                    querySnapshot.docChanges().forEach((change) => {
                        const doc = change.doc;
                        let data = doc.data()
                        let user = { ...data, id: doc.id }
                        switch (change.type) {
                            case "added":
                                userSetup(rootGetters, commit, user)
                                break;
                            case "modified":
                                userSetup(rootGetters, commit, user)
                                break;
                            case "removed":
                                commit("delete", user);
                                break;
                            default:
                                break;
                        }
                    });
                });
                commit('addUnsub', unsub)
            } catch (error) {
                console.log("ERROR TO GET ALL USERS: " + error);
            }
        },
        async addProjectCollection({ rootState }, { userId, name }) {
            const instanceId = rootState.instanceId
            const role = rootState.users.role

            if (instanceId == null) return;
            if (role !== 'consumer') return

            try {
                const projectRef = doc(collection(db, `instances/${instanceId}/users/${userId}/projects`))
                await setDoc(projectRef, {
                    name,
                    createdAt: serverTimestamp(),
                    createdBy: rootState.users.currentUser.uid,
                    addedToWorker: false,
                })
            } catch (error) {
                console.log(error)
            }
        },

        async getRole({ commit, rootState, state }) {
            const instanceId = rootState.instanceId;
            const currentUser = state.currentUser;
            if (instanceId == null) return;
            if (currentUser == null) return;

            try {
                const userDoc = await getDoc(doc(db, `instances/${instanceId}/users`, currentUser.uid));
                let userInfo = null;
                if (userDoc.exists()) {
                    userInfo = userDoc.data();
                    if (userInfo.role) {
                        commit('setCurrentUserRole', userInfo.role);
                    }
                    commit('setUserInfo', userInfo)
                }

                if (state.userInfo.role == "admin") {
                    return
                }

                if (userInfo.consumer) {
                    const querySnapshot = await getDoc(doc(
                        db,
                        `instances/${instanceId}/consumers/${userInfo.consumer}`
                    ));
                    if (querySnapshot.exists()) {
                        let data = querySnapshot.data();
                        commit('setUserInfo', { ...userInfo, ...data })
                        commit('setCurrentUserRole', 'consumer');
                    }
                }

                if (userInfo.worker) {
                    const querySnapshot = await getDoc(doc(
                        db,
                        `instances/${instanceId}/workers/${userInfo.worker}`
                    ));
                    if (querySnapshot.exists()) {
                        let data = querySnapshot.data();
                        commit('setUserInfo', { ...userInfo, ...data })
                        commit('setCurrentUserRole', 'worker');
                    }
                }
            } catch (error) {
                console.log("Error fetching user role: ", error);
            }
        },

        async login({ commit }, { email, password }) {
            try {
                await signInWithEmailAndPassword(
                    auth,
                    email,
                    password,
                );
                commit('setErrorCode', null)

            } catch (error) {
                commit('setErrorCode', error.code)
            }
        },

        async getWorkers({ commit, rootState }) {
            const instanceId = rootState.instanceId;
            const workers = [];

            const workersQuery = query(collection(db, `instances/${instanceId}/invited`), where('role', '==', 'worker'));

            const querySnapshot = await getDocs(workersQuery);
            querySnapshot.forEach((doc) => {
                workers.push({ ...doc.data() });
            });

            commit('setWorkers', workers);
        },

        async recover({ commit, rootState }, email) {
            const instanceId = rootState.instanceId;
            try {
                await RecoverPassword({ email, instance: instanceId })
                commit('setRecoverEmailSent', true)
            } catch (error) {
                console.log(error)
                commit('setRecoverEmailSent', false)
            }
        },

        async register({ rootState, commit }, { email, name, password, consumerId, workerId }) {
            const instanceId = rootState.instanceId
            if (instanceId == null) {
                return
            }
            try {
                const credential = await createUserWithEmailAndPassword(auth, email, password)
                const user = credential.user
                let userData = {}
                let avatar = null
                if (consumerId) {
                    userData.consumer = consumerId
                    avatar = `${instanceId}/consumers/${consumerId}/logo`
                }
                if (workerId) {
                    userData.worker = workerId
                }
                await setDoc(doc(db, `instances/${instanceId}/users/${user.uid}`), userData, { merge: true })
                await setDoc(doc(db, 'users', user.uid), { name, email })
                await setDoc(
                    doc(db, `profiles/${user.uid}`),
                    { avatar, name },
                    { merge: true }
                )
                await updateProfile(user, { displayName: name, email: email })
                // await assignRole({ role: role })

                await user.getIdToken(true);

                commit('setErrorCode', null)
            } catch (error) {
                console.log(error)
                commit('setErrorCode', error.code)
            }
        },

        async delete({ commit, rootState }, id) {
            const instanceId = rootState.instanceId

            try {
                const usersRef = doc(db, `instances/${instanceId}/invited`, id)
                await deleteDoc(usersRef)
                commit('delete', id)

            } catch (error) {
                console.log('ERROR DELETING SERVICE', error)
            }
        },

        async edit({ commit, rootState }, user) {
            const instanceId = rootState.instanceId
            if (instanceId == null) {
                return
            }
            try {
                const userRef = doc(db, `instances/${instanceId}/workers`, user.id);
                await updateDoc(userRef, {
                    name: user.name,
                });
                commit('user', user)
            } catch (error) {
                console.log(error)
            }
        },
        async sendHelpEmail({ rootState }, { subject, body, file }) {
            const instanceId = rootState.instanceId
            if (instanceId == null) {
                return
            }
            const currentUser = rootState.users.currentUser
            if (currentUser == null) {
                return
            }
            try {
                const helpRef = doc(collection(db, `instances/${instanceId}/help`))

                await setDoc(helpRef, {
                    email: currentUser.email,
                    subject,
                    body,
                    file,
                    time: serverTimestamp(),
                    createdAt: serverTimestamp(),
                })
            } catch (error) {
                console.log(error)
            }
        },
    },
};

export default users;
